import { Card, Col, Form, FormListFieldData, InputNumber, Row, Switch, Typography } from 'antd'
import clsx from 'clsx'
import { PtoV3TimelineItem } from 'Modules'

import DatePicker from 'component/date-picker/date-picker'
import { convertNumberToDateTime } from 'utils/datetime'

const { Title } = Typography

interface ITimeLineV3Props {
  form: any
  editable?: boolean
}

interface ITimeLineItem {
  form: any
  phases: PtoV3TimelineItem[]
  field: FormListFieldData
  editable?: boolean
}

/**
 * PTO v3 timelines - same UX as the v2 timelines (no Tier Benefit section), with the
 * ERC-3643 field names: phases[].maxPTOs and the hidden `totalSupply` (token cap).
 * Derived from modules/pto-v3/form/timelines.tsx.
 */
const TimeLineItem = ({ editable, form, phases, field }: ITimeLineItem) => {
  const data = phases[field.name]

  const toggle = data?.enabled

  return (
    <Col span={24}>
      <Card className={'mb-4'} size="default">
        <div
          className={clsx('flex items-center justify-between', {
            'mb-2': toggle,
          })}
        >
          <Title level={5} style={{ margin: 0, textTransform: 'uppercase' }}>
            {data.required ? <span style={{ color: 'red', marginRight: 5 }}>*</span> : null}
            {field.key + 1}. {data.name}
          </Title>
          {data.required ? null : (
            <Switch
              checked={toggle}
              disabled={data.required || !editable}
              onChange={(checked) => {
                const phases = form.getFieldValue('phases')
                phases[field.key].enabled = checked

                // Helper function to find the nearest previous enabled timeline with endTime
                const findPreviousEnabledTimeline = (currentIndex: number) => {
                  for (let i = currentIndex - 1; i >= 0; i--) {
                    if (phases[i].enabled && phases[i].endTime) {
                      return phases[i]
                    }
                  }
                  return null
                }

                // Update startTime for this timeline when enabling
                if (checked && field.key > 0) {
                  const prevPhase = findPreviousEnabledTimeline(field.key)
                  if (prevPhase) {
                    phases[field.key].startTime = prevPhase.endTime
                  }
                }

                // Update all subsequent enabled timelines
                for (let i = field.key + 1; i < phases.length; i++) {
                  if (phases[i].enabled) {
                    const prevPhase = findPreviousEnabledTimeline(i)
                    if (prevPhase) {
                      phases[i].startTime = prevPhase.endTime
                    } else {
                      // No previous enabled timeline, clear startTime
                      phases[i].startTime = null
                    }
                  }
                }

                form.setFieldsValue({ phases })
              }}
            />
          )}
        </div>

        {toggle ? (
          <>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name={[field.name, 'startTime']}
                  label={
                    <span>
                      {data.enabled ? (
                        <span style={{ color: 'red', marginRight: 5 }}>*</span>
                      ) : null}
                      Start Date
                    </span>
                  }
                  style={{ marginBottom: 20 }}
                  rules={[
                    {
                      validator: async (_, value) => {
                        if (!data.enabled) {
                          return
                        } else {
                          if (!value) {
                            throw new Error('Start time is required when timeline is enabled')
                          }

                          const phases = form.getFieldValue('phases')
                          const enabledPhases = phases.filter((p: any) => p.enabled)
                          const currentIndex = enabledPhases.findIndex(
                            (p: any) => p.name === data.name
                          )

                          // Check if startTime >= previous phase's endTime
                          if (currentIndex > 0) {
                            const prevPhase = enabledPhases[currentIndex - 1]
                            if (prevPhase.endTime && value < prevPhase.endTime) {
                              throw new Error(
                                'Start time phải lớn hơn hoặc bằng end time của phase trước'
                              )
                            }
                          }

                          return
                        }
                      },
                    },
                  ]}
                  getValueFromEvent={(date) => (date ? Math.floor(date.valueOf() / 1000) : null)}
                  getValueProps={(value) => ({
                    value: value ? convertNumberToDateTime(value) : null,
                  })}
                >
                  <DatePicker className="w-full" disabled={data.name !== 'upcoming' || !editable} />
                </Form.Item>
              </Col>
              {data.isEndTimeUnlimited ? null : (
                <Col span={12}>
                  <Form.Item
                    name={[field.name, 'endTime']}
                    label={'End Date'}
                    style={{ marginBottom: 20 }}
                    rules={[
                      {
                        required: (data.required || data.enabled) && !data.isEndTimeUnlimited,
                        message: 'End time is required when timeline is enabled',
                      },
                      {
                        validator: async (_, value) => {
                          if (!data.enabled || data.isEndTimeUnlimited) {
                            return
                          }
                          if (!value) {
                            throw new Error('End time is required when timeline is enabled')
                          }
                          const startTime = form.getFieldValue(['phases', field.name, 'startTime'])

                          // Check if endTime > startTime
                          if (startTime && value <= startTime) {
                            throw new Error('End time phải lớn hơn start time')
                          }

                          return
                        },
                      },
                    ]}
                    getValueFromEvent={(date) => (date ? Math.floor(date.valueOf() / 1000) : null)}
                    getValueProps={(value) => ({
                      value: value ? convertNumberToDateTime(value) : null,
                    })}
                  >
                    <DatePicker
                      className="w-full"
                      disabled={!editable}
                      disabledDate={(current) => {
                        const startTime = data.startTime
                        if (!startTime || !current) return false

                        const startDate = new Date(startTime * 1000)
                        startDate.setHours(0, 0, 0, 0)

                        const currentDate = new Date(current.valueOf())
                        currentDate.setHours(0, 0, 0, 0)

                        // Only disable dates strictly before startTime date
                        return currentDate.getTime() < startDate.getTime()
                      }}
                      showTime={{
                        disabledTime: (current) => {
                          const startTime = data.startTime
                          if (!startTime || !current) return {}

                          const startDate = new Date(startTime * 1000)
                          const currentDate = new Date(current.valueOf())

                          // Reset time to compare only dates
                          const startDateOnly = new Date(startDate)
                          startDateOnly.setHours(0, 0, 0, 0)
                          const currentDateOnly = new Date(currentDate)
                          currentDateOnly.setHours(0, 0, 0, 0)

                          // If same day, disable hours/minutes before startTime
                          if (currentDateOnly.getTime() === startDateOnly.getTime()) {
                            const startHour = startDate.getHours()
                            const startMinute = startDate.getMinutes()

                            return {
                              disabledHours: () => {
                                const hours = []
                                for (let i = 0; i < startHour; i++) {
                                  hours.push(i)
                                }
                                return hours
                              },
                              disabledMinutes: (selectedHour: number) => {
                                if (selectedHour === startHour) {
                                  const minutes = []
                                  // Disable minutes up to and including startMinute
                                  for (let i = 0; i <= startMinute; i++) {
                                    minutes.push(i)
                                  }
                                  return minutes
                                }
                                return []
                              },
                            }
                          }

                          return {}
                        },
                      }}
                      onChange={() => {
                        const phases = form.getFieldValue('phases')

                        // Helper function to find the nearest previous enabled timeline with endTime
                        const findPreviousEnabledPhase = (currentIndex: number) => {
                          for (let i = currentIndex - 1; i >= 0; i--) {
                            if (phases[i].enabled && phases[i].endTime) {
                              return phases[i]
                            }
                          }
                          return null
                        }

                        // Update all subsequent enabled timelines
                        for (let i = field.name + 1; i < phases.length; i++) {
                          if (phases[i].enabled) {
                            const prevPhase = findPreviousEnabledPhase(i)
                            if (prevPhase) {
                              phases[i].startTime = prevPhase.endTime
                            } else {
                              phases[i].startTime = null
                            }
                          }
                        }

                        form.setFieldsValue({ phases })
                      }}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
            {data.isPurchasePhase ? (
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name={[field.name, 'maxPTOs']}
                    label={'Number of PTOs for sale'}
                    style={{ marginBottom: 15 }}
                    dependencies={['totalSupply', 'phases']}
                    rules={[
                      {
                        required: data.isPurchasePhase && (data.required || data.enabled),
                        message: 'Number of PTOs is required for purchase phase',
                      },
                      {
                        validator: async (_, value) => {
                          if (!data.isPurchasePhase || !data.enabled) {
                            return
                          }

                          if (value === null || value === undefined || value < 0) {
                            throw new Error('Number of PTOs is required')
                          }

                          // Check if maxPTOs <= totalSupply
                          const totalSupply = form.getFieldValue('totalSupply')
                          if (!totalSupply) {
                            throw new Error(`Please enter total supply`)
                          }
                          if (value > totalSupply) {
                            throw new Error(
                              `Number of PTOs is less than equal to total supply (${totalSupply})`
                            )
                          }

                          const phases = form.getFieldValue('phases')
                          const phasesPurchase = phases.filter(
                            (phase: PtoV3TimelineItem) => phase.enabled && phase.isPurchasePhase
                          )
                          if (phasesPurchase.length === 1) {
                            if (value !== totalSupply) {
                              throw new Error(
                                `Number of PTOs is equal to total supply(${totalSupply})`
                              )
                            }
                          } else {
                            // Mở 2 phase purchase tổng các phase mua bán phải bằng totalSupply
                            const totalPurchase = phasesPurchase.reduce(
                              (total: number, phase: PtoV3TimelineItem) =>
                                total + Number(phase.maxPTOs || 0),
                              0
                            )
                            if (totalPurchase !== totalSupply) {
                              throw new Error(
                                `Total purchase (${totalPurchase}) is equal to total supply(${totalSupply})`
                              )
                            }
                          }

                          return
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      className="w-full"
                      disabled={!editable}
                      placeholder="Enter quantity"
                      precision={0}
                      onChange={async () => {
                        // Đợi một chút để form update value
                        setTimeout(async () => {
                          const phases = form.getFieldValue('phases') || []
                          // Validate lại TẤT CẢ các phase purchase khác
                          for (let i = 0; i < phases.length; i++) {
                            const phase = phases[i]
                            // Chỉ validate các phase purchase đang enabled
                            if (phase?.enabled && phase?.isPurchasePhase) {
                              try {
                                await form.validateFields([['phases', i, 'maxPTOs']])
                              } catch {
                                // Bỏ qua error để tiếp tục validate các field khác
                              }
                            }
                          }
                        }, 0)
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            ) : null}
          </>
        ) : null}
      </Card>
    </Col>
  )
}

const Timelines = (props: ITimeLineV3Props) => {
  const { form, editable = false } = props

  return (
    <fieldset>
      <legend>Timelines</legend>
      <Form.List name="phases">
        {(fields, _, { errors }) => {
          return (
            <>
              {fields.map((field) => {
                const phases = form.getFieldValue('phases')
                return (
                  <TimeLineItem
                    key={Math.random()}
                    field={field}
                    form={form}
                    phases={phases}
                    editable={editable}
                  />
                )
              })}
              <Form.ErrorList errors={errors} />
            </>
          )
        }}
      </Form.List>
    </fieldset>
  )
}

export default Timelines
